import apiClient from './apiClient';
import { getApiBaseUrl } from '@/config/environment';


export interface TimeRange {
  startDate: string;
  endDate: string;
}

export interface DashboardMetrics {
  overview: {
    totalUsers: number;
    totalDrivers: number;
    totalVehicles: number;
    totalBookings: number;
    totalRevenue: number;
    activeBookings: number;
    onlineDrivers: number;
    availableVehicles: number;
  };
  trends: {
    userGrowth: number;
    bookingGrowth: number;
    revenueGrowth: number;
    driverGrowth: number;
  };
  recentActivity: {
    newUsers: number;
    newBookings: number;
    completedBookings: number;
    newDriverApplications: number;
  };
}

export interface BookingAnalytics {
  totalBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  activeBookings: number;
  pendingBookings: number;
  averageBookingValue: number;
  totalRevenue: number;
  bookingsByStatus: Array<{ status: string; count: number; percentage: number }>;
  bookingsByMonth: Array<{ month: string; count: number; revenue: number }>;
  topVehicles: Array<{ vehicleId: string; make: string; model: string; bookings: number; revenue: number }>;
  averageBookingDuration: number;
  cancellationRate: number;
}

export interface UserAnalytics {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  usersByRole: Array<{ role: string; count: number; percentage: number }>;
  userGrowthByMonth: Array<{ month: string; count: number }>;
  topCustomers: Array<{ userId: string; name: string; totalBookings: number; totalSpent: number }>;
  userRetentionRate: number;
  averageSessionDuration: number;
}

export interface DriverAnalytics {
  totalDrivers: number;
  activeDrivers: number;
  onlineDrivers: number;
  pendingApplications: number;
  approvedThisMonth: number;
  rejectedThisMonth: number;
  driversByStatus: Array<{ status: string; count: number; percentage: number }>;
  topDrivers: Array<{ driverId: string; name: string; totalTrips: number; rating: number; earnings: number }>;
  averageRating: number;
  driverUtilizationRate: number;
  monthlyEarnings: Array<{ month: string; totalEarnings: number; avgEarnings: number }>;
}

export interface VehicleAnalytics {
  totalVehicles: number;
  activeVehicles: number;
  availableVehicles: number;
  rentedVehicles: number;
  maintenanceVehicles: number;
  vehiclesByType: Array<{ type: string; count: number; percentage: number }>;
  vehiclesByStatus: Array<{ status: string; count: number; percentage: number }>;
  topVehicles: Array<{ vehicleId: string; make: string; model: string; utilization: number; revenue: number }>;
  averageUtilizationRate: number;
  vehicleConditionScores: Array<{ vehicleId: string; qualityScore: number; verificationScore: number }>;
}

export interface RevenueAnalytics {
  totalRevenue: number;
  monthlyRevenue: number;
  averageBookingValue: number;
  revenueGrowthRate: number;
  revenueByMonth: Array<{ month: string; revenue: number; bookings: number }>;
  revenueByVehicleType: Array<{ type: string; revenue: number; percentage: number }>;
  revenueByRegion: Array<{ region: string; revenue: number; bookings: number }>;
  topEarningVehicles: Array<{ vehicleId: string; make: string; model: string; revenue: number }>;
  paymentMethodStats: Array<{ method: string; count: number; amount: number }>;
}

export interface LiveMetrics {
  onlineUsers: number;
  activeBookings: number;
  onlineDrivers: number;
  pendingBookings: number;
  timestamp: string;
}

export interface MonthlyReport {
  reportPeriod: {
    month: string;
    year: number;
    startDate: string;
    endDate: string;
  };
  dashboardMetrics: DashboardMetrics;
  bookingAnalytics: BookingAnalytics;
  userAnalytics: UserAnalytics;
  generatedAt: string;
}

export type ExportFormat = 'json' | 'csv';
export type AnalyticsType = 'dashboard' | 'bookings' | 'users' | 'drivers' | 'vehicles' | 'revenue';

// New Dashboard Analytics Interface
export interface DashboardAnalytics {
  total: number;
  categories: {
    all: number;
    'self-drive': number;
    'with-driver': number;
    'driver-hire': number;
    hybrid: number;
    rentals: number;
    'yacht-charter': number;
  };
  serviceTypes: {
    rental: number;
    'driver-hire': number;
    hybrid: number;
    fleet: number;
    individual: number;
    company: number;
  };
  bookingTypes: {
    instant: number;
    'approval-required': number;
  };
  revenue: {
    total: number;
    currency: string;
    byCategory: {
      rentals: number;
      'driver-hire': number;
      hybrid: number;
      'yacht-charter': number;
      'vehicle-rental': number;
      'self-drive': number;
      'with-driver': number;
    };
    byServiceType: {
      'self-drive': number;
      'with-driver': number;
      'driver-hire': number;
      hybrid: number;
      'yacht-charter': number;
    };
  };
  statuses: {
    [key: string]: number;
  };
}

// Enhanced Analytics Interface with new backend fields
export interface EnhancedAnalytics {
  total: number;
  categories: {
    all: number;
    'self-drive': number;
    'with-driver': number;
    'driver-hire': number;
    hybrid: number;
    'yacht-charter': number;
  };
  enhancedServiceTypes: {
    self_drive: number;        // NEW: From serviceTypeEnhanced
    with_driver: number;       // NEW: From serviceTypeEnhanced
    chauffeur_driven: number;  // NEW: From serviceTypeEnhanced
    personal_driver: number;   // NEW: From serviceTypeEnhanced
  };
  enhancedServiceCategories: {
    vehicle_rental: number;    // NEW: From serviceCategory
    driver_hire: number;       // NEW: From serviceCategory
    package_service: number;   // NEW: From serviceCategory
    yacht_charter: number;     // NEW: From serviceCategory
  };
  enhancedBookingTypes: {
    instant: number;           // NEW: From approvalType
    approval_required: number; // NEW: From approvalType
    admin_approval: number;    // NEW: From approvalType
    dual_approval: number;     // NEW: From approvalType
  };
  bookingChannels: {
    mobile_app: number;        // NEW: From bookingChannel
    web_platform: number;     // NEW: From bookingChannel
    admin_panel: number;       // NEW: From bookingChannel
    agent_assisted: number;    // NEW: From bookingChannel
  };
  revenue: {
    total: number;
    currency: string;
    byServiceType: {
      self_drive: number;
      with_driver: number;
      chauffeur_driven: number;
    };
    byChannel: {
      mobile_app: number;
      web_platform: number;
      admin_panel: number;
    };
  };
}

class AnalyticsService {
  private baseUrl = '/api/v1/analytics';

  // Get dashboard metrics
  async getDashboardMetrics(timeRange?: TimeRange): Promise<DashboardMetrics> {
    const params = new URLSearchParams();
    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/dashboard?${params.toString()}`);
    return response.data;
  }

  // Get booking analytics
  async getBookingAnalytics(timeRange?: TimeRange): Promise<BookingAnalytics> {
    const params = new URLSearchParams();
    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/bookings?${params.toString()}`);
    return response.data;
  }

  // Get user analytics
  async getUserAnalytics(timeRange?: TimeRange): Promise<UserAnalytics> {
    const params = new URLSearchParams();
    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/users?${params.toString()}`);
    return response.data;
  }

  // Get driver analytics
  async getDriverAnalytics(timeRange?: TimeRange): Promise<DriverAnalytics> {
    const params = new URLSearchParams();
    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/drivers?${params.toString()}`);
    return response.data;
  }

  // Get vehicle analytics
  async getVehicleAnalytics(timeRange?: TimeRange): Promise<VehicleAnalytics> {
    const params = new URLSearchParams();
    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/vehicles?${params.toString()}`);
    return response.data;
  }

  // Get revenue analytics
  async getRevenueAnalytics(timeRange?: TimeRange): Promise<RevenueAnalytics> {
    const params = new URLSearchParams();
    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/revenue?${params.toString()}`);
    return response.data;
  }

  // Get live metrics
  async getLiveMetrics(): Promise<LiveMetrics> {
    const response = await apiClient.get(`${this.baseUrl}/live`);
    return response.data;
  }

  // Generate monthly report
  async generateMonthlyReport(month: number, year: number): Promise<MonthlyReport> {
    const params = new URLSearchParams({
      month: month.toString(),
      year: year.toString()
    });

    const response = await apiClient.get(`${this.baseUrl}/report/monthly?${params.toString()}`);
    return response.data;
  }

  // Export analytics data
  async exportAnalytics(type: AnalyticsType, format: ExportFormat = 'json', timeRange?: TimeRange): Promise<any> {
    const params = new URLSearchParams({
      type,
      format
    });

    if (timeRange) {
      params.append('startDate', timeRange.startDate);
      params.append('endDate', timeRange.endDate);
    }

    const response = await apiClient.get(`${this.baseUrl}/export?${params.toString()}`, {
      responseType: format === 'csv' ? 'blob' : 'json'
    });

    if (format === 'csv') {
      // Create download link for CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${type}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return true;
    }

    return response.data;
  }

  // New Dashboard Analytics Methods
  async getDashboardAnalytics(): Promise<DashboardAnalytics | null> {
    try {
      const API_BASE = getApiBaseUrl();

      // Fetch all bookings from the admin endpoint
      const response = await fetch(`${getApiBaseUrl()}/api/admin/bookings?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`❌ Bookings fetch error ${response.status}:`, await response.text());
        throw new Error('Failed to fetch bookings for analytics');
      }

      const data = await response.json();
      let bookings = [];

      // Handle different response structures
      if (data.success && data.data) {
        bookings = data.data;
      } else if (Array.isArray(data.data)) {
        bookings = data.data;
      } else if (Array.isArray(data)) {
        bookings = data;
      } else if (data.bookings && Array.isArray(data.bookings)) {
        bookings = data.bookings;
      }


      // Log first booking to see data structure
      if (bookings.length > 0) {
      }

      // Calculate analytics from bookings data
      const analytics: DashboardAnalytics = {
        total: bookings.length,
        categories: {
          all: bookings.length,
          'self-drive': 0,
          'with-driver': 0,
          'driver-hire': 0,
          hybrid: 0,
          rentals: 0,
          'yacht-charter': 0,
        },
        serviceTypes: {
          rental: 0,
          'driver-hire': 0,
          hybrid: 0,
          fleet: 0,
          individual: 0,
          company: 0,
        },
        bookingTypes: {
          instant: 0,
          'approval-required': 0,
        },
        revenue: {
          total: 0,
          currency: 'NGN',
          byCategory: {
            rentals: 0,
            'driver-hire': 0,
            hybrid: 0,
            'yacht-charter': 0,
            'vehicle-rental': 0,
            'self-drive': 0,
            'with-driver': 0,
          },
          byServiceType: {
            'self-drive': 0,
            'with-driver': 0,
            'driver-hire': 0,
            hybrid: 0,
            'yacht-charter': 0,
          },
        },
        statuses: {},
      };

      // Process each booking
      bookings.forEach((booking: any, index: number) => {
        // ✅ FIXED: Amount is in payment.amount field
        const amount = parseFloat(
          booking.payment?.amount ||
          booking.totalAmount ||
          booking.amount ||
          booking.pricing?.totalAmount ||
          booking.booking?.amount ||
          booking.total ||
          0
        );

        // Get all possible field values
        const serviceCategory = (booking.serviceCategory || '').toString().toLowerCase();
        // ✅ FIXED: Service type is in booking.type field
        const serviceType = (booking.booking?.type || booking.serviceType || booking.bookingType || booking.type || '').toString().toLowerCase();
        const approvalType = (booking.approvalType || '').toString().toLowerCase();
        const status = (booking.status || '').toString().toLowerCase();

        // Log each booking processing for debugging
        if (index < 3) {
        }

        // Categorize by service type
        let categorized = false;

        // Check for "with-driver" bookings (chauffeur-driven)
        if (serviceType.includes('with-driver') ||
            serviceType.includes('with_driver') ||
            serviceType.includes('chauffeur')) {
          analytics.categories['with-driver']++;
          analytics.revenue.byCategory['with-driver'] += amount;
          analytics.revenue.byServiceType['with-driver'] += amount;
          categorized = true;
        }
        // Check for "driver-hire" (standalone driver)
        else if (serviceCategory.includes('driver-hire') ||
                 serviceCategory.includes('driver_hire') ||
                 serviceType.includes('driver-hire') ||
                 serviceType.includes('driver_hire') ||
                 serviceType.includes('personal-driver') ||
                 serviceType.includes('personal_driver')) {
          analytics.categories['driver-hire']++;
          analytics.revenue.byCategory['driver-hire'] += amount;
          analytics.revenue.byServiceType['driver-hire'] += amount;
          analytics.serviceTypes['driver-hire']++;
          categorized = true;
        }
        // Check for "self-drive" bookings
        else if (serviceType.includes('self-drive') ||
                 serviceType.includes('self_drive') ||
                 serviceCategory.includes('vehicle-rental') ||
                 serviceCategory.includes('vehicle_rental') ||
                 serviceCategory.includes('rental')) {
          analytics.categories['self-drive']++;
          analytics.categories.rentals++;
          analytics.revenue.byCategory['self-drive'] += amount;
          analytics.revenue.byCategory['vehicle-rental'] += amount;
          analytics.revenue.byCategory.rentals += amount;
          analytics.revenue.byServiceType['self-drive'] += amount;
          categorized = true;
        }
        // Check for yacht bookings
        else if (serviceCategory.includes('yacht') || serviceType.includes('yacht')) {
          analytics.categories['yacht-charter']++;
          analytics.revenue.byCategory['yacht-charter'] += amount;
          analytics.revenue.byServiceType['yacht-charter'] += amount;
          categorized = true;
        }
        // Check for hybrid bookings
        else if (serviceCategory.includes('hybrid') || serviceType.includes('hybrid')) {
          analytics.categories.hybrid++;
          analytics.revenue.byCategory.hybrid += amount;
          analytics.revenue.byServiceType.hybrid += amount;
          categorized = true;
        }

        if (!categorized && index < 3) {
        }

        // Count by approval type
        if (approvalType.includes('instant')) {
          analytics.bookingTypes.instant++;
        } else {
          analytics.bookingTypes['approval-required']++;
        }

        // Track statuses
        if (status) {
          analytics.statuses[status] = (analytics.statuses[status] || 0) + 1;
        }

        // Add to total revenue
        analytics.revenue.total += amount;
      });


      return analytics;

    } catch (error) {
      console.error('Error fetching analytics:', error);
      return null;
    }
  }

  async getCategoryCount(category: string): Promise<number> {
    try {
      const analytics = await this.getDashboardAnalytics();
      return analytics?.categories[category as keyof typeof analytics.categories] || 0;
    } catch (error) {
      console.error(`Error fetching ${category} count:`, error);
      return 0;
    }
  }

  // Helper methods for data formatting
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  }

  formatPercentage(value: number): string {
    return `${value.toFixed(1)}%`;
  }

  formatGrowth(value: number): { text: string; color: string; icon: string } {
    const isPositive = value > 0;
    const isNegative = value < 0;
    
    return {
      text: `${isPositive ? '+' : ''}${value.toFixed(1)}%`,
      color: isPositive ? 'success' : isNegative ? 'error' : 'default',
      icon: isPositive ? 'trending_up' : isNegative ? 'trending_down' : 'trending_flat'
    };
  }

  // Chart data formatters
  formatChartData(data: Array<{ [key: string]: any }>, xKey: string, yKey: string, label?: string): any {
    return {
      labels: data.map(item => item[xKey]),
      datasets: [{
        label: label || yKey,
        data: data.map(item => item[yKey]),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1
      }]
    };
  }

  formatPieChartData(data: Array<{ [key: string]: any }>, labelKey: string, valueKey: string): any {
    return {
      labels: data.map(item => item[labelKey]),
      datasets: [{
        data: data.map(item => item[valueKey]),
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)'
        ],
        borderWidth: 1
      }]
    };
  }

  // Date range helpers
  getDateRangePresets(): Array<{ label: string; value: TimeRange }> {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return [
      {
        label: 'Today',
        value: {
          startDate: today.toISOString(),
          endDate: now.toISOString()
        }
      },
      {
        label: 'Last 7 Days',
        value: {
          startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: now.toISOString()
        }
      },
      {
        label: 'Last 30 Days',
        value: {
          startDate: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          endDate: now.toISOString()
        }
      },
      {
        label: 'This Month',
        value: {
          startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
          endDate: now.toISOString()
        }
      },
      {
        label: 'Last Month',
        value: {
          startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString(),
          endDate: new Date(now.getFullYear(), now.getMonth(), 0).toISOString()
        }
      },
      {
        label: 'This Year',
        value: {
          startDate: new Date(now.getFullYear(), 0, 1).toISOString(),
          endDate: now.toISOString()
        }
      }
    ];
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;